import React, { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { SuperCard } from "../../components/SuperAdminLayout";
import { notify } from "../../lib/notify";
import { Save, Mail, CheckCircle2, AlertTriangle } from "lucide-react";

export default function SuperSettingsPage() {
  const [s, setS] = useState(null);
  const [ratePct, setRatePct] = useState("");

  useEffect(() => {
    api.get("/super/settings").then((r) => { setS(r.data); setRatePct((parseFloat(r.data.commission_rate) * 100).toString()); });
  }, []);
  if (!s) return <div className="text-[#8a8a8a] py-20 text-center">Loading…</div>;

  const saveCommission = async () => {
    const v = parseFloat(ratePct);
    if (isNaN(v) || v < 0 || v > 100) return notify.error("Enter 0–100");
    try { await api.put("/super/settings", { commission_rate: v / 100 }); notify.success("Default commission updated"); }
    catch (e) { notify.error(formatErr(e.response?.data?.detail)); }
  };
  const saveMode = async (mode) => {
    setS({ ...s, stripe_mode: mode });
    await api.put("/super/settings", { stripe_mode: mode }); notify.success(`Stripe mode: ${mode}`);
  };
  const saveAnnounce = async () => {
    await api.put("/super/settings", { platform_announcement: s.platform_announcement || "" });
    notify.success("Announcement saved");
  };
  const saveSenders = async () => {
    try {
      await api.put("/super/settings", { from_onboarding: s.from_onboarding || "", from_tickets: s.from_tickets || "" });
      notify.success("Sender addresses saved");
    } catch (e) { notify.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="super-settings">
      <h1 className="text-2xl font-bold">Platform settings</h1>

      <SuperCard className="p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] flex items-center gap-2"><Mail size={14} /> Email sender addresses</h3>
          {s.resend_configured ? (
            <span className="text-xs text-[#86efac] flex items-center gap-1" data-testid="resend-status-ok"><CheckCircle2 size={13} /> Resend connected</span>
          ) : (
            <span className="text-xs text-[#e8c987] flex items-center gap-1" data-testid="resend-status-off"><AlertTriangle size={13} /> Resend not configured (emails logged only)</span>
          )}
        </div>
        <p className="text-sm text-[#8a8a8a] mb-4">Define which verified address each type of email is sent from. Use the format <code className="text-[#bbb]">Name &lt;email@scaretickets.co.uk&gt;</code> or a plain address. The domain must be verified in Resend.</p>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-[#bbb]">Onboarding emails <span className="text-[#7a7a7a]">(account verification, password reset)</span></label>
            <input className="st-input mt-1" placeholder="ScareTickets <onboarding@scaretickets.co.uk>" value={s.from_onboarding || ""} onChange={(e) => setS({ ...s, from_onboarding: e.target.value })} data-testid="settings-from-onboarding" />
          </div>
          <div>
            <label className="text-sm text-[#bbb]">Consumer / ticket emails <span className="text-[#7a7a7a]">(confirmations, reminders, refunds)</span></label>
            <input className="st-input mt-1" placeholder="ScareTickets <tickets@scaretickets.co.uk>" value={s.from_tickets || ""} onChange={(e) => setS({ ...s, from_tickets: e.target.value })} data-testid="settings-from-tickets" />
          </div>
        </div>
        <button className="st-btn mt-4" onClick={saveSenders} data-testid="settings-senders-save"><Save size={14} /> Save sender addresses</button>
      </SuperCard>

      <SuperCard className="p-5">
        <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">Default commission rate</h3>
        <p className="text-sm text-[#8a8a8a] mb-4">Applied to every attraction without a custom rate or active promo.</p>
        <div className="flex items-center gap-2">
          <input className="st-input w-28" type="number" step="0.1" min="0" max="100" value={ratePct} onChange={(e) => setRatePct(e.target.value)} data-testid="settings-commission" /><span>%</span>
          <button className="st-btn" onClick={saveCommission} data-testid="settings-commission-save"><Save size={14} /> Save</button>
        </div>
      </SuperCard>

      <SuperCard className="p-5">
        <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">Stripe mode</h3>
        <p className="text-sm text-[#8a8a8a] mb-4">Test mode uses Stripe test keys. Switch to Live only when live keys are configured.</p>
        <div className="flex gap-2">
          {["test", "live"].map((m) => (
            <button key={m} onClick={() => saveMode(m)} className={`st-btn ${s.stripe_mode === m ? "" : "st-btn-ghost"}`} data-testid={`settings-mode-${m}`}>{m === "test" ? "Test mode" : "Live mode"}</button>
          ))}
        </div>
      </SuperCard>

      <SuperCard className="p-5">
        <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-2">Platform announcement</h3>
        <p className="text-sm text-[#8a8a8a] mb-4">Shows as a banner on every attraction dashboard. Leave empty to hide.</p>
        <textarea className="st-input min-h-[90px]" placeholder="e.g. Scheduled maintenance this Sunday 2–4am." value={s.platform_announcement || ""} onChange={(e) => setS({ ...s, platform_announcement: e.target.value })} data-testid="settings-announcement" />
        <button className="st-btn mt-3" onClick={saveAnnounce} data-testid="settings-announcement-save"><Save size={14} /> Save announcement</button>
      </SuperCard>
    </div>
  );
}
